import { storage } from '@/lib/storage';

/**
 * Thin fetch wrapper for the WordPress REST API.
 *
 * WooCommerce's Store API identifies a shopper's cart with a Cart-Token header
 * instead of cookies, which keeps the headless frontend free of cross-site
 * cookie problems. Responses can carry a fresh Cart-Token and Nonce; both are
 * kept and sent back on the next request.
 */

const BASE = (import.meta.env.VITE_WP_URL || '').replace(/\/$/, '') + '/wp-json';
const TOKEN_KEY = 'odra.cartToken';

let cartToken: string | null = storage.get(TOKEN_KEY);
let nonce: string | null = null;

export class ApiError extends Error {
  constructor(message: string, public code: string, public status: number, public data?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

type Query = Record<string, string | number | undefined>;

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
  query?: Query;
}

const CART_PATH = '/wc/store/v1/cart';
const NONCE_ERRORS = new Set(['woocommerce_rest_missing_nonce', 'woocommerce_rest_invalid_nonce']);

async function request<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  Object.entries(opts.query ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
  });

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (cartToken) headers['Cart-Token'] = cartToken;
  if (nonce) headers['Nonce'] = nonce;

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      credentials: 'omit',
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError('We couldn’t reach the shop. Check your connection and try again.', 'network_error', 0);
  }

  const newToken = res.headers.get('Cart-Token');
  if (newToken && newToken !== cartToken) {
    cartToken = newToken;
    storage.set(TOKEN_KEY, newToken);
  }
  const newNonce = res.headers.get('Nonce') || res.headers.get('X-WC-Store-API-Nonce');
  if (newNonce) nonce = newNonce;

  const data = res.status === 204 ? null : await res.json().catch(() => null);

  if (!res.ok) {
    const code: string = (data && data.code) || 'http_' + res.status;
    // A missing or stale nonce is recoverable: fetch the cart for a fresh one, then retry once.
    if (!retried && NONCE_ERRORS.has(code) && path !== CART_PATH) {
      await request(CART_PATH, {}, true).catch(() => undefined);
      return request<T>(path, opts, true);
    }
    // An invalid or expired cart token: forget it so the next request starts a new cart.
    if (code.includes('cart_token')) {
      cartToken = null;
      storage.remove(TOKEN_KEY);
    }
    const message = stripTags((data && data.message) || 'Something went wrong. Please try again.');
    throw new ApiError(message, code, res.status, data?.data);
  }
  return data as T;
}

function stripTags(s: string) {
  const d = document.createElement('div');
  d.innerHTML = s;
  return (d.textContent || s).trim();
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>(path, { query, signal }),
  post: <T>(path: string, body: unknown = {}) => request<T>(path, { method: 'POST', body }),
};
